import { useMemo } from "react";
import { useFormik, type FormikProps } from "formik";
import * as yup from "yup";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { DatePicker, FormField, Select, TextField } from "@outpave/ds";
import { Columns, Stack } from "components/layout/reusableLayouts";
import {
  type ContactSubscription,
  type SubscriptionCadence,
} from "utils/contacts/recurringBilling";
import { CADENCE_LABEL_KEY } from "utils/contacts/billingFormat";

export interface SubscriptionEditValues {
  planName: string;
  /** Major units, because that is what the operator types. */
  amount: string;
  cadence: SubscriptionCadence;
  /** yyyy-mm-dd, which is what a date input reads and writes. */
  nextChargeOn: string;
}

/** Epoch ms to the yyyy-mm-dd a date input wants, in UTC to match the record. */
const toDateInput = (epochMs: number | null): string =>
  epochMs ? new Date(epochMs).toISOString().slice(0, 10) : "";

/**
 * The form's state, held by the drawer rather than by this component.
 *
 * The package takes one `footer` on the Drawer, so Save lives on the parent
 * and needs something to submit. Associating it by `form={id}` is correct HTML
 * and does not survive jsdom, which implements no `requestSubmit`, so the
 * parent holds the formik and calls it. Same shape as the `renderFooter()`
 * switch OUT-1406 landed on for the upload flow.
 */
/**
 * Exported so the rules are tested directly. Driving these fields through
 * jsdom is unreliable: neither `userEvent.type` nor `fireEvent.change`
 * propagates into formik through the DS TextField there, though both work in
 * a browser, so the schema is asserted here and the typing path is verified
 * on screen.
 */
export const subscriptionEditSchema = (
  t: (key: string) => string,
): yup.ObjectSchema<Record<string, unknown>> =>
  yup.object({
    planName: yup.string().trim().required(t("Plan name is required")),
    amount: yup
      .number()
      // yup casts "" to NaN, so without this an empty field reports "must be
      // a number", which is not what an operator who cleared it did wrong.
      .transform((value, original) => (original === "" ? undefined : value))
      .typeError(t("Amount must be a number"))
      .moreThan(0, t("Amount must be more than zero"))
      .required(t("Amount is required")),
    cadence: yup.string().required(t("Frequency is required")),
    nextChargeOn: yup.string().required(t("Next invoice date is required")),
  });

export const useSubscriptionEditForm = (
  subscription: ContactSubscription | null,
  onSubmit: (values: SubscriptionEditValues) => void,
): FormikProps<SubscriptionEditValues> => {
  const { t } = useTranslation();

  // Memoised, and this is load-bearing. The drawer is mounted with a null
  // subscription until a row is picked, so the form has to reseed when one
  // arrives, which needs `enableReinitialize`. But that effect fires whenever
  // `initialValues` changes identity, and an object literal changes on every
  // render, so an unmemoised one resets the form under the operator between
  // keystrokes. Both halves of that were live: without reinitialize the fields
  // opened blank, with it plus a fresh object every edit was discarded.
  const initialValues = useMemo<SubscriptionEditValues>(
    () => ({
      planName: subscription?.planName ?? "",
      amount: subscription ? (subscription.amountCents / 100).toFixed(2) : "",
      cadence: subscription?.cadence ?? "MONTHLY",
      nextChargeOn: toDateInput(subscription?.nextChargeAt ?? null),
    }),
    [subscription],
  );

  return useFormik<SubscriptionEditValues>({
    enableReinitialize: true,
    initialValues,
    validationSchema: subscriptionEditSchema(t),
    onSubmit,
  });
};

interface SubscriptionEditFormProps {
  formik: FormikProps<SubscriptionEditValues>;
}

const SubscriptionEditForm: React.FC<SubscriptionEditFormProps> = ({
  formik,
}) => {
  const { t } = useTranslation();

  // undefined rather than false when there is no error: the DS resolves a
  // field's message as `errorText ?? helperText`, so a `false` shows neither.
  const errorFor = (field: keyof SubscriptionEditValues) =>
    formik.touched[field] && formik.errors[field]
      ? String(formik.errors[field])
      : undefined;

  const cadenceOptions = Object.entries(CADENCE_LABEL_KEY).map(
    ([id, labelKey]) => ({ id, label: t(labelKey) }),
  );

  return (
    <Stack gap="var(--space-5)">
      <Typography variant="body2" sx={{ color: "var(--color-text-tertiary)" }}>
        {t("SubscriptionEditIntro")}
      </Typography>

      <TextField
        fullWidth
        required
        label={t("Plan name")}
        name="planName"
        value={formik.values.planName}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        errorText={errorFor("planName")}
      />

      <Columns count={2} gap="var(--space-4)">
        <TextField
          fullWidth
          required
          label={t("Amount per cycle")}
          name="amount"
          value={formik.values.amount}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          leading={<Typography variant="body2">$</Typography>}
          inputMode="decimal"
          errorText={errorFor("amount")}
        />

        <Select
          fullWidth
          required
          label={t("Frequency")}
          value={formik.values.cadence}
          options={cadenceOptions}
          onChange={(value) =>
            formik.setFieldValue("cadence", value ?? formik.values.cadence)
          }
          errorText={errorFor("cadence")}
        />
      </Columns>

      {/* The DS picker owns the field and calendar but carries no label,
          helper or required marker, so FormField supplies them. It takes no
          `id` either, so the label cannot be bound with htmlFor: that is the
          one DS control whose label is not associated with it. */}
      <FormField
        fullWidth
        label={t("Next invoice date")}
        required
        error={Boolean(errorFor("nextChargeOn"))}
        errorText={errorFor("nextChargeOn")}
      >
        <DatePicker
          // A guarded dayjs: an unset date is null, not dayjs(""), which puts
          // an Invalid Date in the field before anything has been picked.
          value={
            formik.values.nextChargeOn
              ? dayjs(formik.values.nextChargeOn)
              : null
          }
          // Kept as the yyyy-mm-dd the record round-trips through. Parsing and
          // formatting are both local, so the string is stable across a round
          // trip even though the fixture stores UTC.
          onChange={(value) =>
            formik.setFieldValue(
              "nextChargeOn",
              value?.isValid() ? value.format("YYYY-MM-DD") : "",
            )
          }
          onBlur={() => formik.setFieldTouched("nextChargeOn", true)}
          error={Boolean(errorFor("nextChargeOn"))}
        />
      </FormField>
    </Stack>
  );
};

export default SubscriptionEditForm;
